import React, {useEffect, useState} from "react";
import {css, Theme} from "@emotion/react";

import {WideContent} from "@pg-design/grid-module";
import {pt} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {isEmpty} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";

import {NearbyOffersPanel} from "../../nearby_offers/NearbyOffersPanel";
import {NearbyOffersPanelPlaceholder} from "../../nearby_offers/NearbyOffersPanelPlaceholder";
import {Country} from "../../region/types/Country";
import {getRequestStateFromRtkQuery} from "../../store/utils/get_request_state_from_rtk_query";
import {neighborhoodOffersClickHit} from "../../tracking/algolytics_hits/neighborhood_offers_click_hit";
import {neighborhoodOffersViewHit} from "../../tracking/algolytics_hits/neighborhood_offers_view_hit";
import {ViewType} from "../../view_type/ViewType";
import {useGetOfferListQuery} from "../api/get_offer_list";
import {offerUrlBuilder} from "../helpers/friendly_offer_list/offer_url_builder";
import {NearbyOffersMoreCard} from "../list/components/NearbyOffersMoreCard";
import {hitGTMOfferViewItemList} from "../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../tracking/offer_box_gtm_events";
import {OfferType} from "../types/OfferType";
import {getDetailPageNearbyOffersPayload} from "../utils/get_detail_page_nearby_offers_payload";

interface IProps {
    viewType: ViewType;
    className?: string;
    offer: {
        id: number;
        type: OfferType;
        region: {
            country: Country;
            name_declension_where: string;
            slug: string;
            id: number;
            full_name: string;
            short_name_reverted: string;
        };
        vendor: {
            id: number;
            name: string;
            slug: string;
        };
    };
}

const NEARBY_OFFERS_WRAPPER_ID = "nearby-offers-wrapper";
export const DetailNearbyOffers: React.FunctionComponent<IProps> = (props) => {
    const {offer, viewType} = props;

    const [shouldFetchNearbyOffers, setShouldFetchNearbyOffers] = useState(false);
    useElementWasVisible({
        ids: [NEARBY_OFFERS_WRAPPER_ID],
        onElementVisible: () => {
            setShouldFetchNearbyOffers(true);
        },
        once: true,
        threshold: 0.05
    });
    const {
        data: nearbyOffersResponse,
        isSuccess,
        isUninitialized,
        isLoading,
        isFetching,
        isError
    } = useGetOfferListQuery(getDetailPageNearbyOffersPayload(offer), {
        skip: !shouldFetchNearbyOffers
    });
    const nearbyOffers = nearbyOffersResponse ? nearbyOffersResponse.results : [];

    // Neighborhood offers hits
    useEffect(() => {
        if (!isEmpty(nearbyOffers)) {
            neighborhoodOffersViewHit(
                viewType,
                nearbyOffers.map((off: {id: number}) => off.id)
            );
            hitGTMOfferViewItemList({
                offers: nearbyOffers,
                listId,
                listName: listingHeadline
            });
        }
    }, [nearbyOffers?.map((off: {id: number}) => off.id).join(",")]);
    const onNeighborhoodBoxClick = (e: React.MouseEvent, offerId: number) => neighborhoodOffersClickHit(viewType, offerId);
    const onNeighborhoodLastClick = () => neighborhoodOffersClickHit(viewType, null);

    const abroadOffer = offer.region.country !== Country.POLAND;
    const {friendlySlug, countrySlug} = offerUrlBuilder({type: offer.type, region: offer.region.slug, country: offer.region.country});

    const url = abroadOffer
        ? rpAppLink.offer.listFriendlyAbroad({
              friendlySlug: friendlySlug,
              country: countrySlug,
              abroadRegionSlug: offer.region.slug
          })
        : rpAppLink.offer.listFriendly({friendlySlug: friendlySlug});

    //  Render
    if (isSuccess && nearbyOffers.length === 0) {
        return null;
    }

    const listingHeadline = "Oferty, które mogą Cię zainteresować";
    const listId = "oferty_ktore_moga_cie_zainteresowac";

    return (
        <div css={wrapStyle} className={props.className} data-id={NEARBY_OFFERS_WRAPPER_ID}>
            <WideContent>
                <Text variant="headline_4" as="h2" css={headingStyle}>
                    {listingHeadline}
                </Text>
            </WideContent>
            {!isSuccess ? (
                <NearbyOffersPanelPlaceholder />
            ) : (
                nearbyOffers.length >= 1 && (
                    <NearbyOffersPanel
                        offersNearby={nearbyOffersResponse.results}
                        gtmMeta={{
                            listId,
                            listName: listingHeadline,
                            source: "additional",
                            tileEvents: {
                                imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                            }
                        }}
                        lastElement={
                            <a href={url} target="_blank" onClick={onNeighborhoodLastClick}>
                                <NearbyOffersMoreCard>
                                    Zobacz wszystkie oferty <br />
                                    {abroadOffer ? "z zagranicy" : offer.region.name_declension_where}
                                </NearbyOffersMoreCard>
                            </a>
                        }
                        onBoxClick={onNeighborhoodBoxClick}
                        modalQuery={{
                            isInitialized: !isUninitialized,
                            isSuccess,
                            isLoading: isLoading || isFetching,
                            isError,
                            requestState: getRequestStateFromRtkQuery({isSuccess, isLoading, isFetching, isError}),
                            data: nearbyOffers,
                            count: nearbyOffers.length,
                            page: nearbyOffersResponse.page,
                            pageSize: nearbyOffersResponse.page_size
                        }}
                    />
                )
            )}
        </div>
    );
};

const wrapStyle = (theme: Theme) => css`
    ${pt(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${pt(4)};
    }
`;

const headingStyle = (theme: Theme) => css`
    margin-bottom: 2.4rem;
    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 3.6rem;
    }
`;
import React, {useEffect, useState} from "react";
import {css, Theme} from "@emotion/react";

import {WideContent} from "@pg-design/grid-module";
import {pt} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {useElementWasVisible} from "@pg-mono/hooks";
import {isEmpty} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";

import {NearbyOffersPanel} from "../../nearby_offers/NearbyOffersPanel";
import {NearbyOffersPanelPlaceholder} from "../../nearby_offers/NearbyOffersPanelPlaceholder";
import {Country} from "../../region/types/Country";
import {getRequestStateFromRtkQuery} from "../../store/utils/get_request_state_from_rtk_query";
import {neighborhoodOffersClickHit} from "../../tracking/algolytics_hits/neighborhood_offers_click_hit";
import {neighborhoodOffersViewHit} from "../../tracking/algolytics_hits/neighborhood_offers_view_hit";
import {ViewType} from "../../view_type/ViewType";
import {useGetOfferListQuery} from "../api/get_offer_list";
import {offerUrlBuilder} from "../helpers/friendly_offer_list/offer_url_builder";
import {NearbyOffersMoreCard} from "../list/components/NearbyOffersMoreCard";
import {hitGTMOfferViewItemList} from "../tracking/ecommerce_events/offer/hit_gtm_offer_view_item_list";
import {offerBoxTracking} from "../tracking/offer_box_gtm_events";
import {OfferType} from "../types/OfferType";
import {getDetailPageNearbyOffersPayload} from "../utils/get_detail_page_nearby_offers_payload";

interface IProps {
    viewType: ViewType;
    className?: string;
    offer: {
        id: number;
        type: OfferType;
        region: {
            country: Country;
            name_declension_where: string;
            slug: string;
            id: number;
            full_name: string;
            short_name_reverted: string;
        };
        vendor: {
            id: number;
            name: string;
            slug: string;
        };
    };
}

const NEARBY_OFFERS_WRAPPER_ID = "nearby-offers-wrapper";
export const DetailNearbyOffers: React.FunctionComponent<IProps> = (props) => {
    const {offer, viewType} = props;

    const [shouldFetchNearbyOffers, setShouldFetchNearbyOffers] = useState(false);
    useElementWasVisible({
        ids: [NEARBY_OFFERS_WRAPPER_ID],
        onElementVisible: () => {
            setShouldFetchNearbyOffers(true);
        },
        once: true,
        threshold: 0.05
    });
    const {
        data: nearbyOffersResponse,
        isSuccess,
        isUninitialized,
        isLoading,
        isFetching,
        isError
    } = useGetOfferListQuery(getDetailPageNearbyOffersPayload(offer), {
        skip: !shouldFetchNearbyOffers
    });
    const nearbyOffers = nearbyOffersResponse ? nearbyOffersResponse.results : [];

    // Neighborhood offers hits
    useEffect(() => {
        if (!isEmpty(nearbyOffers)) {
            neighborhoodOffersViewHit(
                viewType,
                nearbyOffers.map((off: {id: number}) => off.id)
            );
            hitGTMOfferViewItemList({
                offers: nearbyOffers,
                listId,
                listName: listingHeadline
            });
        }
    }, [nearbyOffers?.map((off: {id: number}) => off.id).join(",")]);
    const onNeighborhoodBoxClick = (e: React.MouseEvent, offerId: number) => neighborhoodOffersClickHit(viewType, offerId);
    const onNeighborhoodLastClick = () => neighborhoodOffersClickHit(viewType, null);

    const abroadOffer = offer.region.country !== Country.POLAND;
    const {friendlySlug, countrySlug} = offerUrlBuilder({type: offer.type, region: offer.region.slug, country: offer.region.country});

    const url = abroadOffer
        ? rpAppLink.offer.listFriendlyAbroad({
              friendlySlug: friendlySlug,
              country: countrySlug,
              abroadRegionSlug: offer.region.slug
          })
        : rpAppLink.offer.listFriendly({friendlySlug: friendlySlug});

    //  Render
    if (isSuccess && nearbyOffers.length === 0) {
        return null;
    }

    const listingHeadline = "Oferty, które mogą Cię zainteresować";
    const listId = "oferty_ktore_moga_cie_zainteresowac";

    return (
        <div css={wrapStyle} className={props.className} data-id={NEARBY_OFFERS_WRAPPER_ID}>
            <WideContent>
                <Text variant="headline_4" as="h2" css={headingStyle}>
                    {listingHeadline}
                </Text>
            </WideContent>
            {!isSuccess ? (
                <NearbyOffersPanelPlaceholder />
            ) : (
                nearbyOffers.length >= 1 && (
                    <NearbyOffersPanel
                        offersNearby={nearbyOffersResponse.results}
                        gtmMeta={{
                            listId,
                            listName: listingHeadline,
                            source: "additional",
                            tileEvents: {
                                imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                            }
                        }}
                        lastElement={
                            <a href={url} target="_blank" onClick={onNeighborhoodLastClick}>
                                <NearbyOffersMoreCard>
                                    Zobacz wszystkie oferty <br />
                                    {abroadOffer ? "z zagranicy" : offer.region.name_declension_where}
                                </NearbyOffersMoreCard>
                            </a>
                        }
                        onBoxClick={onNeighborhoodBoxClick}
                        modalQuery={{
                            isInitialized: !isUninitialized,
                            isSuccess,
                            isLoading: isLoading || isFetching,
                            isError,
                            requestState: getRequestStateFromRtkQuery({isSuccess, isLoading, isFetching, isError}),
                            data: nearbyOffers,
                            count: nearbyOffers.length,
                            page: nearbyOffersResponse.page,
                            pageSize: nearbyOffersResponse.page_size
                        }}
                    />
                )
            )}
        </div>
    );
};

const wrapStyle = (theme: Theme) => css`
    ${pt(3)};

    @media (min-width: ${theme.breakpoints.md}) {
        ${pt(4)};
    }
`;

const headingStyle = (theme: Theme) => css`
    margin-bottom: 2.4rem;
    @media (min-width: ${theme.breakpoints.md}) {
        margin-bottom: 3.6rem;
    }
`;
